/*!

=========================================================
* Comprehend PT - Email Verification
=========================================================

* Shown immediately after signup. The account exists but is unusable until the
* user clicks the link in the verification email, so this page's only jobs are
* to explain that and to let them re-send the email if it never arrived.

=========================================================

*/

// reactstrap components
import { Col, Form } from 'reactstrap';

import { useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import getParamFromURL from '../api/getParamFromURL';
import { GlobalContext } from '../api/GlobalContext';

// The signup request already sent one email, so start the button locked out.
// The backend rate limits this too - this is just to stop impatient clicking.
const RESEND_COOLDOWN_SECONDS = 60;

const VerifyEmailSent = () => {
  // Get the API
  const { apiClient } = useContext(GlobalContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Register hands its answers forward in router state (in memory, never the
  // URL) so "Wrong address?" can drop the user back on the signup form with
  // everything they typed still filled in - they only re-fix the email. Absent
  // on a deep-linked / login-redirect arrival, in which case we prefill the
  // one thing we do know: the email shown on this page.
  const signupDraft = location.state?.signupDraft;

  // The email is passed through from Register/Login. If it is missing (someone
  // deep-linked or refreshed after clearing the query string) we ask for it.
  // Lowercased on the way in: the backend stores addresses lowercased, so
  // echoing back whatever casing the user typed shows them an address that
  // isn't quite the one we hold.
  const [email, setEmail] = useState(
    getParamFromURL('email', '').trim().toLowerCase(),
  );
  const emailFromURL = useRef(!!getParamFromURL('email', '')).current;

  // Empty until something actually happens - the heading and body already say
  // "check your email", so a third line repeating it is just noise.
  const [labelText, setLabelText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [cooldown, setCooldown] = useState(
    emailFromURL ? RESEND_COOLDOWN_SECONDS : 0,
  );

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (cooldown <= 0) return undefined;

    const interval = setInterval(() => {
      setCooldown((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [cooldown]);

  function handleResend(e) {
    e.preventDefault();

    //RegEx for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    //check if email format is valid
    if (!emailRegex.test(email)) {
      setLabelText(
        'Invalid email format. Please provide a valid email address',
      );
      return;
    }

    setIsSending(true);
    setLabelText('Sending a new verification email...');

    apiClient
      .sendVerificationEmail(email)
      .then((data) => {
        if (data && 'error' in data) {
          setLabelText(data['error']);
        } else {
          setLabelText(
            'Sent. If an unverified account exists for this address, a new verification link is on its way.',
          );
          setCooldown(RESEND_COOLDOWN_SECONDS);
        }
        setIsSending(false);
      })
      .catch((e) => {
        setLabelText('Error: ' + e);
        setIsSending(false);
      });
  }

  // "Wrong address?" - a mistyped signup email can never be verified (nobody
  // owns that inbox) and resending to it does nothing, so the fix is to go back
  // to signup and re-submit under the right address. The account's primary key
  // is the email, so re-registering is what creates a correctly-keyed account;
  // the stale unverified row is inert. We carry the earlier answers back so
  // only the email has to change - the password is deliberately not carried.
  function handleWrongAddress() {
    navigate('/auth/register', {
      state: { signupDraft: signupDraft || { email } },
    });
  }

  const resendDisabled = isSending || cooldown > 0 || !email;

  // "Prefer a text message?" - the alternate path for someone who can't get
  // to the inbox we mailed. Collapsed by default; email stays the default
  // flow for everyone else.
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [isSendingSms, setIsSendingSms] = useState(false);

  function handleSendSms(e) {
    e.preventDefault();
    if (!email) {
      setPhoneError('Enter your email address above first.');
      return;
    }
    if (!phone.trim()) {
      setPhoneError('Enter a phone number.');
      return;
    }

    setPhoneError('');
    setIsSendingSms(true);

    apiClient
      .sendVerificationSms(email, phone.trim())
      .then((data) => {
        setIsSendingSms(false);
        if (data && 'error' in data) {
          setPhoneError(data['error']);
          return;
        }
        // The real phone number is passed via router state, not the URL, so
        // it never lands in the address bar, browser history, or analytics.
        navigate('/auth/verify/phone', {
          state: {
            email,
            phone: phone.trim(),
            maskedPhone: data && data['phone'],
          },
        });
      })
      .catch((e) => {
        setIsSendingSms(false);
        setPhoneError('Error: ' + e);
      });
  }

  return (
    <>
      <Col lg="5" md="7">
        <div className="cptx-auth">
          <div className="cptx-card">
            <div className="cptx-card-body">
              <div className="cptx-icon-tile cptx-icon-tile-center">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M4 6h16v12H4V6Z"
                    stroke="#2dce89"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M4.5 6.5 12 13l7.5-6.5"
                    stroke="#2dce89"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h2 className="cptx-title" style={{ textAlign: 'center' }}>
                Check your email
              </h2>
              <p className="cptx-subtitle" style={{ textAlign: 'center' }}>
                We sent a verification link
                {email ? (
                  <>
                    {' '}
                    to <strong style={{ wordBreak: 'break-all' }}>{email}</strong>
                  </>
                ) : (
                  ''
                )}
                . Open it to activate your account and finish signing in.
                {location.state?.isStudent && (
                  <>
                    <br />
                    Thanks for being a student! Your account is free for 2 years.
                  </>
                )}
              </p>

              {email ? (
                <div style={{ textAlign: 'center', marginBottom: '18px' }}>
                  <button
                    type="button"
                    className="cptx-change-trigger"
                    onClick={handleWrongAddress}
                  >
                    Wrong address?
                  </button>
                </div>
              ) : null}

              {labelText ? <div className="cptx-status">{labelText}</div> : null}

              <Form role="form" onSubmit={handleResend}>
                {!emailFromURL && (
                  <div className="cptx-field">
                    <label className="cptx-label">Email</label>
                    <input
                      className="cptx-input"
                      style={{ marginTop: '6px' }}
                      placeholder="Your Comprehend PT account email"
                      type="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) =>
                        setEmail(e.target.value.trim().toLowerCase())
                      }
                    />
                  </div>
                )}

                <button
                  className="cptx-btn-outline"
                  type="submit"
                  disabled={resendDisabled}
                >
                  {isSending
                    ? 'Sending...'
                    : cooldown > 0
                      ? `Resend email in ${cooldown}s`
                      : 'Resend email'}
                </button>
                <p className="cptx-link-line" style={{ marginTop: '10px' }}>
                  {cooldown > 0
                    ? "Didn't arrive? You can send another shortly."
                    : "Didn't arrive? Send it again."}
                </p>
              </Form>

              <hr className="cptx-hr" />
              {!showPhoneForm ? (
                <div style={{ textAlign: 'center' }}>
                  <button
                    type="button"
                    className="cptx-hint-link"
                    onClick={() => setShowPhoneForm(true)}
                  >
                    Prefer a text message instead?
                  </button>
                </div>
              ) : (
                <Form role="form" onSubmit={handleSendSms}>
                  <p className="cptx-link-line" style={{ marginBottom: '14px' }}>
                    We'll text a 6-digit code to verify you instead.
                  </p>
                  <div className="cptx-field">
                    <label className="cptx-label">Phone number</label>
                    <input
                      className="cptx-input"
                      style={{ marginTop: '6px' }}
                      placeholder="(555) 123-4567"
                      type="tel"
                      autoComplete="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>
                  {phoneError ? (
                    <div className="cptx-error-text" style={{ textAlign: 'center', marginBottom: '10px' }}>
                      {phoneError}
                    </div>
                  ) : null}
                  <button
                    className="cptx-btn-outline"
                    type="submit"
                    disabled={isSendingSms}
                  >
                    {isSendingSms ? 'Sending...' : 'Text me a code'}
                  </button>
                </Form>
              )}

              <hr className="cptx-hr" />
              <p className="cptx-link-line">
                Not there? Check spam and promotions, or{' '}
                <a href="mailto:support@comprehendpt.ai">contact support</a>.
              </p>
              <p className="cptx-footer-note">
                <Link to="/auth/login">Back to sign in</Link>
              </p>
            </div>
          </div>
        </div>
      </Col>
    </>
  );
};

export default VerifyEmailSent;
